import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { Button } from "@/components/ui/button";
import { DateField } from "@/components/common/DateField";
import { Empty, Kpi, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";

const WINDOWS = [["Next 30d", 0, 30], ["Next 90d", 0, 90], ["Last 30d", -30, 0],
                 ["Last 90d", -90, 0], ["Last 12M", -365, 0], ["Whole year", -180, 180]];

const shift = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export const Utilisation = () => {
  const [win, setWin] = useState("Next 90d");
  const [range, setRange] = useState({ from: shift(0), to: shift(90) });
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/property/utilisation",
      { params: { date_from: range.from, date_to: range.to } });
    setData(res.data);
  }, [range]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const pick = (label, from, to) => {
    setWin(label);
    setRange({ from: shift(from), to: shift(to) });
  };

  if (!data) return null;
  const c = data.counters;

  return (
    <div className="space-y-4" data-testid="utilisation-panel">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-wrap gap-1.5">
          {WINDOWS.map(([label, from, to]) => (
            <Button key={label} size="sm" variant={win === label ? "default" : "outline"}
                    data-testid={`utilisation-window-${label.replace(/\s+/g, "-")}`}
                    onClick={() => pick(label, from, to)}
                    className={`h-7 rounded-full px-3 text-[11px] ${win === label
                      ? "bg-primary text-primary-foreground" : "border-border"}`}>
              {label}
            </Button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <DateField value={range.from} testId="utilisation-from-input" clearable={false}
                     onChange={(v) => { setWin("custom"); setRange({ ...range, from: v }); }}
                     className="h-7 w-[140px] text-[11px]" />
          <DateField value={range.to} testId="utilisation-to-input" clearable={false}
                     onChange={(v) => { setWin("custom"); setRange({ ...range, to: v }); }}
                     className="h-7 w-[140px] text-[11px]" />
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid="utilisation-period">
          {data.period.from} → {data.period.to} · {data.period.open_hours_per_day} bookable hours a day
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi testId="kpi-occupancy" label="Occupancy" value={`${c.occupancy}%`} accent="violet"
             sub={`${c.hours} hours booked`} />
        <Kpi testId="kpi-util-income" label="Hire income" accent="emerald"
             value={money(c.income, "INR", true)} />
        <Kpi testId="kpi-yield" label="Yield per hour" accent="sky"
             value={money(c.yield_per_hour, "INR")} />
        <Kpi testId="kpi-util-bookings" label="Bookings" value={c.bookings}
             sub={`${c.cancelled} cancelled`} />
        <Kpi testId="kpi-cancel-rate" label="Cancellation rate" accent="amber"
             value={`${c.cancellation_rate}%`} />
        <Kpi testId="kpi-peak" label="Busiest" value={c.busiest_day}
             accent="teal" sub={`peak start ${c.busiest_hour}`} />
      </div>

      <Panel testId="panel-room-utilisation" kicker="Room by room"
             title="Occupancy, income and yield">
        <Table testId="utilisation-table"
               head={<><Th>Space</Th><Th>Number</Th><Th>Bookings</Th><Th>Hours</Th><Th>Occupancy</Th>
                 <Th>Income</Th><Th>Yield / hour</Th><Th>Internal</Th><Th>Cancelled</Th></>}>
          {data.rooms.length === 0 && <Empty colSpan={9} label="No rooms configured" />}
          {data.rooms.map((r) => (
            <tr key={r.code} className="row-hover" data-testid={`utilisation-row-${r.code}`}>
              <Td className="text-[11.5px] font-medium text-foreground">{r.name}
                <div className="font-mono text-[10px] text-muted-foreground">{r.property_code}</div></Td>
              <Td className="font-mono text-[11px]">{r.room_number || "—"}</Td>
              <Td className="font-mono text-[11px]">{r.bookings}</Td>
              <Td className="font-mono text-[11px]">{r.hours}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                    <div className="h-full rounded-full bg-primary"
                         style={{ width: `${Math.min(r.occupancy, 100)}%` }} />
                  </div>
                  <span className="font-mono text-[11px]">{r.occupancy}%</span>
                </div>
              </Td>
              <Td className="font-mono text-[11px]">{money(r.income, r.currency)}</Td>
              <Td className="font-mono text-[11px]">{money(r.yield_per_hour, r.currency)}</Td>
              <Td className="font-mono text-[11px]">{r.internal}</Td>
              <Td className="font-mono text-[11px]">{r.cancelled}</Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel testId="panel-peak-days" kicker="Demand" title="Hours booked by day of the week">
          <div className="h-[220px] px-3 py-3" data-testid="peak-days-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="day" tickFormatter={(d) => d.slice(0, 3)}
                       tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} width={28} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", fontSize: 11,
                  border: "1px solid hsl(var(--border))", borderRadius: 10 }} />
                <Bar dataKey="hours" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel testId="panel-top-hirers" kicker="Revenue" title="Top hirers in this window">
          <Table testId="top-hirers-table"
                 head={<><Th>Hirer</Th><Th>Bookings</Th><Th>Hours</Th><Th>Income</Th></>}>
            {data.top_hirers.length === 0 && <Empty colSpan={4} label="No chargeable hires yet" />}
            {data.top_hirers.map((h) => (
              <tr key={h.name} className="row-hover" data-testid={`top-hirer-${h.name}`}>
                <Td className="text-[11.5px] text-foreground">{h.name}</Td>
                <Td className="font-mono text-[11px]">{h.bookings}</Td>
                <Td className="font-mono text-[11px]">{h.hours}</Td>
                <Td className="font-mono text-[11px]">{money(h.income, "INR")}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>
    </div>
  );
};
